import React, {useEffect, useState} from "react";
import classnames from "classnames";
import Tooltip from "@mui/material/Tooltip";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";

import {useCalcContext} from "../../contexts/CalcContext";
import {useUserContext} from "../../contexts/UserContext";
import {useListingsStore} from "../../stores/listingsStore";
import {useSavedDealershipsStore} from "../../stores/savedDealershipsStore";
import {useThemeStore} from "../../stores/themeStore";
import {ListingsSelectionContext} from "../../contexts/ListingsSelectionContext";
import {SearchBar} from "../ListingsPage/SearchBar";
import {Sidebar} from "../ListingsPage/Sidebar";
import {MainContent} from "../ListingsPage/MainContent";
import {SavedDealershipsPanel} from "../ListingsPage/SavedDealershipsPanel";

import "../ListingsPage/ListingsPage.scss";
import "./CalcListingsDrawer.scss";

// Collapsible inventory search beneath the calc boxes. Only expandable once a
// full year/make/model/trim is chosen; expanding (or changing the vehicle while
// open) seeds the shared listings store and runs a search centered on the
// dealer zip. Selecting a listing fills the calculator's MSRP + VIN.
export const CalcListingsDrawer = () => {
    const {carState, calcState, applyListing, isUsed} = useCalcContext()
    const {isFeatureFlagEnabled} = useUserContext()
    const theme = useThemeStore((state) => state.theme)
    const [isOpen, setIsOpen] = useState(false)

    const setFilters = useListingsStore((state) => state.setFilters)
    const fetchSavedDealerships = useSavedDealershipsStore((state) => state.fetchSaved)

    // Inventory is searched at the model level now (no trim), so a full
    // year/make/model is enough to open the drawer.
    const canExpand = Boolean(carState.year && carState.make && carState.model)
    const dealerZip = calcState.dealer_zipcode

    // Re-seed + re-search whenever the selected vehicle (or dealer zip) changes
    // while the drawer is open — including each time it's opened.
    const seedKey = `${carState.year}|${carState.make}|${carState.model}|${dealerZip || ''}`

    useEffect(function seedAndSearch(){
        if (!isOpen || !canExpand) return

        (async () => {
            // Load saved dealerships first so the single seed fetch carries the
            // "My Dealers" filter.
            await fetchSavedDealerships()

            const newFilters = {
                // Model-level search: no trim, and no `car` (which would pin the
                // exact trim config via model_code) — clear any stale car.
                searchTags: {
                    year: String(carState.year),
                    make: carState.make,
                    model: carState.model,
                },
                car: null,
                // Version/engine/body style facet values are model-specific, so a
                // vehicle change makes them stale — clear them on (re)seed.
                versions: [],
                engines: [],
                body_types: [],
                // Year multi-select preselected to the calculator's vehicle year.
                years: carState.year ? [String(carState.year)] : [],
                // Preselect New/Used to match the calculator's vehicle.
                inventory_types: [isUsed ? 'used' : 'new'],
            }
            if (dealerZip) {
                newFilters.location = {zipcode: dealerZip, latitude: null, longitude: null}
            }

            setFilters(newFilters, true)
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, seedKey])

    // If the selection stops being complete (e.g. Reset), collapse.
    useEffect(function collapseWhenInvalid(){
        if (!canExpand && isOpen) setIsOpen(false)
    }, [canExpand, isOpen])

    // Selecting a listing fills the calculator's MSRP (VIN drives the decode) and
    // returns to the top with the Invoice input focused.
    const selection = {
        onSelectListing: ({msrp, vin}) => {
            applyListing({vin, msrp})
            document.getElementById('CalculatePage')?.scrollTo({top: 0, behavior: 'smooth'})
            window.scrollTo({top: 0, behavior: 'smooth'})
            setTimeout(() => {
                document.getElementById('calc-invoice-input')?.focus({preventScroll: true})
            }, 0)
        },
    }

    const vehicleLabel = canExpand
        ? [carState.year, carState.make, carState.model].filter(Boolean).join(' ')
        : ''

    // Gated behind the 'listings' feature flag (checked after hooks so hook order
    // stays stable).
    if (!isFeatureFlagEnabled('listings')) return null

    return (
        <div className="calc-listings-drawer">
            <Tooltip
                title={canExpand ? '' : 'Select a year, make and model to search inventory'}
                arrow
                placement="top"
            >
                {/* span wrapper so the tooltip still shows on the disabled button */}
                <span className="calc-listings-toggle-wrap">
                    <button
                        type="button"
                        className={classnames('calc-listings-toggle', {open: isOpen, disabled: !canExpand})}
                        onClick={() => canExpand && setIsOpen((v) => !v)}
                        disabled={!canExpand}
                    >
                        {/* Sizes come from CSS (14px, matching the address rows),
                            so no fontSize prop. */}
                        <DirectionsCarOutlinedIcon className="car-icon"/>
                        <span className="calc-listings-toggle-label">
                            Search Listings
                            {vehicleLabel && <span className="calc-listings-toggle-sub">{vehicleLabel}</span>}
                        </span>
                        <ExpandMoreIcon className="chevron"/>
                    </button>
                </span>
            </Tooltip>

            {
                isOpen && canExpand &&
                <div className="calc-listings-body">
                    <ListingsSelectionContext.Provider value={selection}>
                        <div id="ListingsPage" data-theme={theme} style={{minHeight: 'unset'}}>
                            <SearchBar/>
                            <div className="layout">
                                <Sidebar/>
                                <MainContent/>
                                <SavedDealershipsPanel/>
                            </div>
                        </div>
                    </ListingsSelectionContext.Provider>
                </div>
            }
        </div>
    )
}
